import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { omitBy, isEmpty } from 'lodash';
import { useApi } from '@/composables/useApi';

import useEditorStore from '@/stores/editor';
import { usePeachy } from '@/composables/usePeachy';

export function generateKeyForChange({ page_key, key, subkey }) {
	return [page_key, key, subkey].filter(Boolean).join('_');
}

export default defineStore('editorChanges', () => {
	const VueRoute = useRoute();
	/** @type {{ type: string, key: string, value: string, status: string, subkey: string }{}} */

	const peachy = usePeachy();
	const schemas = ref({});

	const savingChanges = ref(false);
	const saveDomain = ref(null);
	const saveRoute = ref(null);
	const saveStatus = ref('hide');
	const saveError = ref(null);

	const localChanges = ref({});
	const localRemovals = ref({});

	const stagedSuggestions = ref([]);

	const hasLocalChanges = computed(() => {
		return (
			Object.keys(localChanges.value).length > 0 ||
			Object.keys(localRemovals.value).length > 0
		);
	});

	function addChange({
		type,
		override_type,
		value,
		status = 'approved',
		key,
		subkey = undefined,
		page_key = undefined,
		allowed_terms = [],
		description = '',
	}) {
		const index = generateKeyForChange({ key, subkey, page_key });
		if (!description) {
			description = key;
		}

		if (!override_type && !!type) {
			override_type = type;
		}

		const temp = {
			override_type,
			value,
			status,
			key,
			subkey,
			page_key,
			allowed_terms,
			editorChangeKey: index,
			description,
		};
		localChanges.value[index] = temp;
	}

	function removeChange({ key, subkey = undefined, page_key = undefined }) {
		const index = generateKeyForChange({ key, subkey, page_key });
		if (localChanges.value[index]) {
			delete localChanges.value[index];
		}
	}

	function addRemoval({ type, override_type, key, subkey = undefined, page_key = undefined }) {
		const index = generateKeyForChange({ key, subkey, page_key });
		if (!override_type && !!type) {
			override_type = type;
		}
		const temp = {
			override_type,
			key,
			subkey,
			page_key,
			editorChangeKey: index,
		};
		localRemovals.value[index] = temp;
	}
	function removeRemoval({ key, subkey, page_key }) {
		const index = generateKeyForChange({ key, subkey, page_key });
		if (localRemovals.value[index]) {
			delete localRemovals.value[index];
		}
	}

	function requiresImageUpload({ override_type, value }) {
		if (!value) {
			return false;
		}

		const imageOverrideTypes = [
			'agent_avatar',
			'office_banner',
			'staff_avatar',
			'jobs_team_images',
			'mission_tab_images',
			'team_tab_images',
			'locations_image',
			'qlp_agent_avatar',
		];
		const isImageOverride = imageOverrideTypes.includes(override_type);
		const isNotString = typeof value !== 'string';
		return isImageOverride && isNotString;
	}

	async function uploadImage(override) {
		if (!requiresImageUpload(override)) {
			return override;
		}

		const domainName = VueRoute.params.domainName;
		const agentID = VueRoute.params.agentID;

		const form = new FormData();
		form.append('domain_name', domainName);
		form.append('image_type', override.override_type);
		form.append('image_ext', override.value.type.split('/')[1]);
		form.append('content_type', override.value.type);
		form.append('file', override.value);

		const { data } = await useApi(`api/agents/${agentID}/image_upload/`, {
			message: 'There was a problem uploading that image.',
		})
			.post(form)
			.json();
		override.value = data.value?.fileUrl;
		return override;
	}

	async function updateCopyContent(categories) {
		peachy('contentoverride', { path: null, payload: { categories } });
		const editorStore = useEditorStore();
		const params = new URLSearchParams();
		for (let category of categories) {
			params.append('category', category);
		}

		try {
			const { error } = await useApi(
				`api/domains/${VueRoute.params.domainName}/copy/refresh/?${params.toString()}`,
				{
					message: 'There was an issue updating the content.',
					headers: { 'content-type': 'application/json' },
				}
			).put();
			if (error?.value) {
				throw error.value;
			}

			await Promise.all([editorStore.updateEditor(), editorStore.updateConfig()]);
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}
	async function saveArticleBlacklist(article_blacklist) {
		try {
			if (!article_blacklist) {
				throw new ReferenceError('Article blacklist is not defined');
			}
			const editorStore = useEditorStore();

			await useApi(`api/domains/${VueRoute.params.domainName}/article_blacklist/`, {
				message: `The new list of hidden articles for ${VueRoute.params.domainName} did not save properly`,
			}).put({
				article_blacklist,
			});
			await editorStore.getArticleBlacklist();
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async function deleteContentOverride({ key, subkey, page_key }) {
		const editorStore = useEditorStore();
		const branch = editorStore.core.value?.meta?.branch ?? 'master';
		const params = new URLSearchParams(omitBy({ branch, key, subkey, page_key }, isEmpty));
		await useApi(
			`api/domains/${VueRoute.params.domainName}/overrides/0/?${params.toString()}`,
			{
				message: 'There was an issue deleting that override.',
			}
		).delete();
	}
	async function saveSingleChange({ key, subkey, page_key }) {
		/*
		 * WARNING: this does not reload editor data by design.
		 * Components that use this function (e.g. AgentProfileImage)
		 * will need to re-initialize editor data after calling.value
		 */
		saveStatus.value = 'saving';
		savingChanges.value = true;

		const editorStore = useEditorStore();
		const branch = editorStore.core.value?.meta?.branch ?? 'master';
		const domainName = VueRoute.params.domainName;

		try {
			const changeKey = generateKeyForChange({ key, subkey, page_key });
			const change = localChanges.value[changeKey];

			if (!change) {
				throw new ReferenceError(`Targeted change is not defined for ${changeKey}`);
			}

			if (change.override_type === 'articles_blacklist') {
				// article blacklists are special little snowflakes
				await saveArticleBlacklist(change.value);
			} else {
				// First we need to upload any image and update the override's value to the url
				// then we can upload the actual override
				const override = await uploadImage(change);
				await useApi(`api/domains/${domainName}/overrides/`, {
					message: 'There was an issue saving that override.',
				}).post({ ...override, branch });
			}

			removeChange({ key, subkey, page_key });
			saveStatus.value = 'saved';
		} catch (error) {
			saveStatus.value = 'error';
			return Promise.reject(error);
		} finally {
			setTimeout(() => {
				saveStatus.value = 'hide';
				savingChanges.value = false;
			}, 6000);
		}
	}
	async function saveAllChanges() {
		saveStatus.value = 'saving';
		savingChanges.value = true;
		saveError.value = null;

		const editorStore = useEditorStore();
		const branch = editorStore.core.value?.meta?.branch ?? 'master';
		const domainName = VueRoute.params.domainName;

		try {
			// first we need to handle article blacklist changes specially
			var changes = [];
			for (var change of Object.values(localChanges.value)) {
				if (change.override_type === 'articles_blacklist') {
					await saveArticleBlacklist(change.value);
				} else {
					changes.push(change);
				}
			}

			// then we need to upload any images and update the override values to the urls
			changes = await Promise.all(changes.map(uploadImage));
			const removals = Object.values(localRemovals.value);

			// next we batch all the creates & deletes together
			const params = new URLSearchParams({ branch });
			const batchOperations = [
				...changes.map(data => ({ ...data, operation: 'create' })),
				...removals.map(data => ({ ...data, operation: 'delete' })),
			];
			await useApi(`api/domains/${domainName}/overrides/batch/?${params.toString()}`, {
				message: 'There was an issue saving the overrides.',
				headers: { 'content-type': 'application/json' },
			}).post(JSON.stringify(batchOperations));

			saveStatus.value = 'saved';
			await this.$reset();
			await editorStore.initializeEditor();
		} catch (error) {
			saveError.value = error;
			saveStatus.value = 'error';
			return Promise.reject(error);
		} finally {
			savingChanges.value = false;
			if (saveStatus.value !== 'error') {
				saveStatus.value = 'hide';
			}
		}
	}

	async function rejectSuggestion(pk, reason) {
		const editorStore = useEditorStore();
		const domainName = VueRoute.params.domainName;

		await useApi(`api/domains/${domainName}/overrides/${pk}/?status=pending`, {
			message: 'There was an issue rejecting that suggestion.',
		}).patch({ status: 'denied', status_detail: reason });
		await editorStore.updateConfig();
	}

	async function acceptSuggestion(pk, value, modifiedByReviewer) {
		saveStatus.value = 'saving';
		savingChanges.value = true;
		saveError.value = null;

		const editorStore = useEditorStore();

		const domainName = VueRoute.params.domainName;
		try {
			await useApi(`api/domains/${domainName}/overrides/${pk}/?status=pending`, {
				message: 'There was an issue saving that suggestion.',
			}).patch({ value, status: 'approved', modified_by_reviewer: modifiedByReviewer });

			saveStatus.value = 'saved';
			await this.$reset();
			await editorStore.initializeEditor();
		} catch (error) {
			saveError.value = error;
			saveStatus.value = 'error';
			return Promise.reject(error);
		} finally {
			savingChanges.value = false;
			if (saveStatus.value !== 'error') {
				saveStatus.value = 'hide';
			}
		}
	}

	async function applyStagedSuggestions() {
		saveStatus.value = 'saving';
		savingChanges.value = true;
		saveError.value = null;

		const editorStore = useEditorStore();

		const domainName = VueRoute.params.domainName;
		const branch = editorStore.core.value?.meta?.branch ?? 'master';
		try {
			const params = new URLSearchParams({ branch });
			const batchOperations = [
				...stagedSuggestions.value.map(data => ({ ...data, operation: 'update' })),
			];
			await useApi(`api/domains/${domainName}/overrides/batch/?${params.toString()}`, {
				message: 'There was an issue saving the overrides.',
				headers: { 'content-type': 'application/json' },
			}).post(JSON.stringify(batchOperations));

			saveStatus.value = 'saved';
			stagedSuggestions.value = [];
			await this.$reset();
			await editorStore.initializeEditor();
		} catch (error) {
			saveError.value = error;
			saveStatus.value = 'error';
			return Promise.reject(error);
		} finally {
			savingChanges.value = false;
			if (saveStatus.value !== 'error') {
				saveStatus.value = 'hide';
			}
		}
	}

	async function controlSavingChangesSnackbar({
		status = 'hide',
		domain = null,
		route = null,
		error = null,
	}) {
		/*
		 this only controls the display of the savingChangesSnackbar component.
		 valid status states:
		 ['saving','saved','error','hide']
		 */
		saveStatus.value = status;
		saveDomain.value = domain;
		saveRoute.value = route;
		saveError.value = error;

		if (status !== 'saving') {
			setTimeout(() => {
				saveStatus.value = 'hide';
				saveDomain.value = null;
				saveRoute.value = null;
				saveError.value = null;
			}, 6000);
		}

		if (error) {
			console.error(error);
		}
	}

	return {
		schemas,

		savingChanges,
		saveDomain,
		saveRoute,
		saveStatus,
		saveError,

		localChanges,
		localRemovals,

		stagedSuggestions,

		hasLocalChanges,

		addChange,
		removeChange,
		addRemoval,
		removeRemoval,
		deleteContentOverride,
		saveSingleChange,
		saveAllChanges,
		saveArticleBlacklist,
		rejectSuggestion,
		acceptSuggestion,
		applyStagedSuggestions,

		controlSavingChangesSnackbar,
		updateCopyContent,

		//testing
		uploadImage,
	};
});
